import {SessionMeta} from './types';
import {CONFIG, DEFAULT_CHAT_MODEL_ID, ModelId, OCR_MODEL_ID} from './config';

export const DEFAULT_CHAT_LABEL = 'Новий чат';

export function chatTitleFromText(text: string): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= 50) return normalized;

  const wordBoundary = normalized.lastIndexOf(' ', 48);
  const end = wordBoundary >= 24 ? wordBoundary : 49;
  return `${normalized.slice(0, end).trim()}…`;
}

function storedChatTitle(sessionId: string): string {
  try {
    const raw = localStorage.getItem(`${CONFIG.storageKeys.chatPrefix}${sessionId}`);
    const messages = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(messages)) return '';

    for (const message of messages) {
      if (message?.role !== 'user') continue;
      const content = typeof message.content === 'string' ? message.content : '';
      const attachmentName = Array.isArray(message.files) && typeof message.files[0]?.name === 'string'
        ? message.files[0].name
        : '';
      const title = chatTitleFromText(content || attachmentName);
      if (title) return title;
    }
  } catch {}
  return '';
}

export function sessionModelId(session: Pick<SessionMeta, 'mode' | 'modelId'>): string {
  if (session.mode === 'ocr') return OCR_MODEL_ID;
  const configuredModel = CONFIG.models.find(model => model.id === session.modelId);
  return configuredModel?.mode === 'chat' ? configuredModel.id : DEFAULT_CHAT_MODEL_ID;
}

function normalizeSession(session: SessionMeta): SessionMeta {
  const mode = session.mode || 'chat';
  const storedTitle = mode === 'chat' && (!session.label || session.label === DEFAULT_CHAT_LABEL)
    ? storedChatTitle(session.id)
    : '';
  return {
    ...session,
    label: storedTitle || session.label || DEFAULT_CHAT_LABEL,
    mode,
    modelId: sessionModelId({...session, mode}),
  };
}

export function loadSessions(): SessionMeta[] {
  try {
    const raw = localStorage.getItem(CONFIG.storageKeys.sessions);
    const sessions: SessionMeta[] = raw ? JSON.parse(raw) : [];
    return sessions.map(normalizeSession);
  } catch { return []; }
}

export function saveSessions(sessions: SessionMeta[]) {
  localStorage.setItem(CONFIG.storageKeys.sessions, JSON.stringify(sessions));
}

export function deleteSession(id: string) {
  const sessions = loadSessions().filter(s => s.id !== id);
  saveSessions(sessions);
  localStorage.removeItem(`${CONFIG.storageKeys.chatPrefix}${id}`);
}

export function createModelSession(modelId: ModelId = DEFAULT_CHAT_MODEL_ID): SessionMeta {
  const model = CONFIG.models.find(candidate => candidate.id === modelId)!;
  return {
    id: newId(),
    type: 'free_form',
    label: model.mode === 'ocr' ? model.name : DEFAULT_CHAT_LABEL,
    createdAt: Date.now(),
    mode: model.mode,
    modelId,
  };
}

export function activateModelSession(
  sessions: SessionMeta[], activeId: string | null, modelId: ModelId,
): {sessions: SessionMeta[]; activeId: string; created: boolean} {
  const active = sessions.find(session => session.id === activeId);
  if (active && sessionModelId(active) === modelId) {
    return {sessions, activeId: active.id, created: false};
  }
  const created = createModelSession(modelId);
  return {sessions: [created, ...sessions], activeId: created.id, created: true};
}

export function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}
